import React, { useEffect, useRef } from 'react';
import { Box, Group, Modal, ScrollArea, Text } from '@mantine/core';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import { ReleaseNotes } from '@repo/services';
import 'md-block';
import { IconNotes } from '@tabler/icons-react';

/**
 * A modal that displays release notes for a specific app version, auto-opens when a new version is detected, and tracks the last-seen version in local storage.
 */
export const ReleaseNotesModal = ({
  appVersion,
  appKey,
  releaseNotes,
  openCloseTrigger
}: {
  appVersion: string;
  appKey: string;
  releaseNotes: ReleaseNotes;
  openCloseTrigger?: (handlers: { open: () => void; close: () => void; toggle: () => void }) => void;
}) => {
  const [opened, releaseModalHandlers] = useDisclosure(false);
  // keep a stable reference of handlers for external trigger
  const handlersRef = useRef(releaseModalHandlers);
  handlersRef.current = releaseModalHandlers;

  // provide handlers to external trigger once on mount
  useEffect(() => {
    if (openCloseTrigger) openCloseTrigger(handlersRef.current);
  }, []);

  const [seenVersion, setSeenVersion] = useLocalStorage<string>({
    key: `${appKey}-version`,
    defaultValue: ''
  });

  // auto open modal if new version with description is available
  useEffect(() => {
    // avoid loop: do nothing if already opened
    if (opened) return;
    const target = releaseNotes?.releases.find((i) => i.versionName == appVersion);
    const hasDescription = !!target?.description;
    if (appVersion && hasDescription && seenVersion !== appVersion) {
      if (!target) return; // ensure version exists in notes
      releaseModalHandlers.open();
    }
  }, [appVersion, releaseNotes, seenVersion, opened]);

  return (
    <Modal
      opened={opened}
      onClose={() => {
        // mark as seen on close
        const versionId = appVersion;
        if (versionId) setSeenVersion(versionId);
        releaseModalHandlers.close();
      }}
      title={<Group justify={'flex-start'}>
        <IconNotes />
        <Text fw={'bold'} size={'xl'}>
          Release Notes
        </Text>
      </Group>
      }
      size='xl'
      centered
    >
      <ScrollArea>
        {releaseNotes?.releases.map((releaseNote) => {
          const { releaseDate, versionName, description } = releaseNote;
          return (
            <Box key={versionName} p={15}>
              <Group
                style={{ borderBottom: '1px dotted' }}
                c={'--mantine-color-Gray-2'}
                align={'baseline'}
                gap={10}
                justify={'space-between'}
              >
                <Text size={'1.5em'} c={'cyan'}>
                  {versionName}
                </Text>
                <Text c={'gray.5'} size={'.5em'}>{releaseDate}</Text>
              </Group>
              <md-block>{description}</md-block>
            </Box>
          );
        })}
      </ScrollArea>
    </Modal>
  );
};

export default ReleaseNotesModal;